/**
 * Data hook for assigning/unassigning a chat's custom (sub) catalog.
 *
 * Wraps `updateSubCatalogChats`, the catalog-service mutation the legacy admin
 * uses (create/remove lists of `{ chatId, subCatalogId }`). The mutation only
 * returns validation errors, so a `GetChatData` refetch keeps the chat's
 * `getSubCatalogsForChat` (current assignment) in sync after a change.
 */

import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  UpdateSubCatalogChatsMutation,
  useUpdateSubCatalogChatsMutation,
} from '../../../generated';

interface UpdateSubCatalogChatsParams {
  chatId: string;
  createIds: string[];
  removeIds: string[];
}

export interface UseUpdateSubCatalogChatsConfig {
  onError?: (error: Error) => void;
  /**
   * Suppress this hook's own error toast and only invoke `onError`. Used by the
   * create flow so a single upper layer owns the user-facing notifications;
   * defaults to `false` so the Edit-chat Settings usage keeps its toast.
   */
  silent?: boolean;
}

interface UseUpdateSubCatalogChatsResponse {
  updateSubCatalogChats: (params: UpdateSubCatalogChatsParams) => Promise<void>;
  isUpdating: boolean;
}

export const getSubCatalogMutationError = (
  response:
    | UpdateSubCatalogChatsMutation['updateSubCatalogChats']
    | null
    | undefined
): string | null => {
  if (response?.__typename === 'AuthorizationFailure') {
    return (
      response.message ||
      'You are not authorized to update the catalog for this chat.'
    );
  }

  if (
    response?.__typename === 'UpdateSubCatalogChatsResult' &&
    response.errors.length > 0
  ) {
    return response.errors
      .map((error) => {
        return error.reason || `Invalid sub-catalog ${error.subCatalogId}`;
      })
      .join(', ');
  }

  if (!response) {
    return 'Catalog update returned no result.';
  }

  return null;
};

export const useUpdateSubCatalogChats = (
  config: UseUpdateSubCatalogChatsConfig = {}
): UseUpdateSubCatalogChatsResponse => {
  const { onError, silent = false } = config;
  const { addNotification } = useNotification();
  const [mutate, { loading }] = useUpdateSubCatalogChatsMutation({
    errorPolicy: 'none',
    refetchQueries: ['GetChatData'],
    // Await the GetChatData refetch so the mutate promise resolves only after
    // the chat's current sub-catalog assignment is reconciled. Without this,
    // an immediate retry after a partial save (catalog ok, a later op failed)
    // would recompute the catalog diff from stale `chatData` and re-send the
    // same `createIds` while the refetch is still in flight.
    awaitRefetchQueries: true,
  });

  const updateSubCatalogChats = useCallback(
    async ({ chatId, createIds, removeIds }: UpdateSubCatalogChatsParams) => {
      try {
        const result = await mutate({
          variables: {
            create: createIds.map((subCatalogId) => ({
              chatId,
              subCatalogId,
            })),
            remove: removeIds.map((subCatalogId) => ({
              chatId,
              subCatalogId,
            })),
          },
        });

        const response = result.data?.updateSubCatalogChats;
        const message = getSubCatalogMutationError(response);

        if (message) {
          throw new Error(message);
        }
      } catch (updateError: unknown) {
        const error =
          updateError instanceof Error
            ? updateError
            : new Error('Catalog update failed.');
        onError?.(error);
        if (!silent) {
          addNotification({
            title: 'Catalog Update Failed',
            message: error.message,
            type: 'error',
          });
        }
        throw error;
      }
    },
    [mutate, addNotification, onError, silent]
  );

  return {
    updateSubCatalogChats,
    isUpdating: loading,
  };
};
